// components/SupportTicket.js - VERSION CORRIGÉE
import React, { useState } from 'react';
import api from '../services/api'; // ✅ Import de l'instance api configurée
import { useTranslation } from 'react-i18next';
import './SupportTicket.css';

const SupportTicket = () => {
    const { t } = useTranslation(['support', 'common', 'errors']);
    
    const [formData, setFormData] = useState({
        category: '',
        subject: '',
        message: '',
        context_data: null
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errors, setErrors] = useState({});

    const categories = [
        { value: 'bug_report', label: t('support:categories.bug_report'), priority: 'normal' },
        { value: 'feature_request', label: t('support:categories.feature_request'), priority: 'normal' },
        { value: 'user_report', label: t('support:categories.user_report'), priority: 'urgent' },
        { value: 'support_question', label: t('support:categories.support_question'), priority: 'normal' },
        { value: 'payment_issue', label: t('support:categories.payment_issue'), priority: 'high' },
        { value: 'other', label: t('support:categories.other'), priority: 'low' }
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.category) {
            newErrors.category = t('support:validation.category_required');
        }

        if (!formData.subject || formData.subject.trim().length < 5) {
            newErrors.subject = t('support:validation.subject_min');
        }

        if (!formData.message || formData.message.trim().length < 10) {
            newErrors.message = t('support:validation.message_min');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // ✅ CORRECTION : Utilisation de l'instance api au lieu d'axios
            const response = await api.post(
                '/support/ticket', // ✅ URL relative
                {
                    category: formData.category,
                    subject: formData.subject.trim(),
                    message: formData.message.trim(),
                    context_data: formData.context_data
                }
                // ✅ Pas besoin de headers, géré par l'intercepteur
            );

            if (response.data.success) {
                setSubmitStatus({
                    type: 'success',
                    message: t('support:messages.ticket_created'),
                    ticketNumber: response.data.ticket.ticket_number,
                    estimatedResponse: response.data.ticket.estimated_response_time
                });

                // Réinitialiser le formulaire
                setFormData({
                    category: '',
                    subject: '',
                    message: '',
                    context_data: null
                });
            }

        } catch (error) {
            console.error('❌ Ticket creation error:', error);
            
            let errorMessage = t('support:messages.creation_error');
            
            // Gestion des erreurs spécifiques
            if (error.response) {
                // Erreur HTTP
                errorMessage = error.response.data?.message || errorMessage;
            } else if (error.request) {
                // Pas de réponse du serveur
                errorMessage = t('support:messages.network_error') || 'Impossible de contacter le serveur';
            } else {
                // Erreur de configuration
                errorMessage = error.message;
            }
            
            setSubmitStatus({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="support-ticket-container">
            <div className="support-ticket-card">
                {/* Header */}
                <div className="support-header">
                    <h2 className="support-title">📬 {t('support:header.title')}</h2>
                    <p className="support-subtitle">
                        {t('support:header.subtitle')}
                    </p>
                </div>

                <button 
                    onClick={() => window.history.back()} 
                    className="support-back-btn"
                >
                    ← {t('support:back_button')}
                </button>

                {/* Status Message */}
                {submitStatus && (
                    <div className={`support-status support-status-${submitStatus.type}`}>
                        <p className="support-status-message">
                            {submitStatus.type === 'success' ? '✅ ' : '❌ '}
                            {submitStatus.message}
                        </p>
                        {submitStatus.type === 'success' && (
                            <div className="support-status-details">
                                <p>{t('support:messages.ticket_number')}: <strong>{submitStatus.ticketNumber}</strong></p>
                                <p>{t('support:messages.estimated_response')}: <strong>{submitStatus.estimatedResponse}</strong></p>
                            </div>
                        )}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="support-form">
                    {/* Category */}
                    <div className="support-form-group">
                        <label className="support-label">{t('support:form.category_label')} *</label>
                        <select
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            className={`support-select ${errors.category ? 'support-input-error' : ''}`}
                        >
                            <option value="">-- {t('support:form.select_category')} --</option>
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </option>
                            ))}
                        </select>
                        {errors.category && (
                            <p className="support-error-text">{errors.category}</p>
                        )}
                    </div>

                    {/* Subject */}
                    <div className="support-form-group">
                        <label className="support-label">{t('support:form.subject_label')} *</label>
                        <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder={t('support:form.subject_placeholder')}
                            maxLength={200}
                            className={`support-input ${errors.subject ? 'support-input-error' : ''}`}
                        />
                        <div className="support-input-footer">
                            {errors.subject ? (
                                <p className="support-error-text">{errors.subject}</p>
                            ) : (
                                <p className="support-hint-text">{t('support:form.subject_hint')}</p>
                            )}
                            <p className="support-counter-text">
                                {formData.subject.length}/200
                            </p>
                        </div>
                    </div>

                    {/* Message */}
                    <div className="support-form-group">
                        <label className="support-label">{t('support:form.message_label')} *</label>
                        <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder={t('support:form.message_placeholder')}
                            rows={6}
                            maxLength={5000}
                            className={`support-textarea ${errors.message ? 'support-input-error' : ''}`}
                        />
                        <div className="support-input-footer">
                            {errors.message ? (
                                <p className="support-error-text">{errors.message}</p>
                            ) : (
                                <p className="support-hint-text">{t('support:form.message_hint')}</p>
                            )}
                            <p className="support-counter-text">
                                {formData.message.length}/5000
                            </p>
                        </div>
                    </div>

                    {/* Category-specific alerts */}
                    {formData.category === 'user_report' && (
                        <div className="support-alert support-alert-urgent">
                            <p>
                                <strong>🚨 {t('support:alerts.user_report.title')}:</strong> {t('support:alerts.user_report.message')}
                            </p>
                        </div>
                    )}

                    {formData.category === 'bug_report' && (
                        <div className="support-alert support-alert-info">
                            <p><strong>💡 {t('support:alerts.bug_report.title')}:</strong> {t('support:alerts.bug_report.message')}</p>
                            <ul>
                                <li>{t('support:alerts.bug_report.tip1')}</li>
                                <li>{t('support:alerts.bug_report.tip2')}</li>
                                <li>{t('support:alerts.bug_report.tip3')}</li>
                            </ul>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`support-submit-btn ${isSubmitting ? 'support-submit-btn-disabled' : ''}`}
                    >
                        {isSubmitting ? (
                            <span className="support-btn-content">
                                <span className="support-spinner"></span>
                                {t('support:form.submitting')}
                            </span>
                        ) : (
                            `📨 ${t('support:form.submit_button')}`
                        )}
                    </button>
                </form>

                {/* Footer */}
                <div className="support-footer">
                    <p>{t('support:footer.response_time')}: <strong>24-48h</strong></p>
                </div>
            </div>
        </div>
    );
};

export default SupportTicket;